import { Link } from 'react-router-dom';
import { DocSection, kbd } from '../../../pages/shell';
import { DocShot } from '../../DocShot';

export const toc = [
  { id: 'overview', label: 'Overview' },
  { id: 'activating', label: 'Activating a key' },
  { id: 'one-machine', label: 'How many machines you can use' },
  { id: 'offline', label: 'Works offline' },
  { id: 'blocked-search', label: 'When search is blocked' },
  { id: 'upgrade', label: 'Trial → paid upgrade' },
];

export default function Body() {
  return (
    <>
      <DocSection id="overview" title="Overview">
        <p>
          Every install starts with a <strong>free trial — 40 searches, keyed to your email</strong>{' '}
          — no card required. A license key unlocks unlimited searching. Both states, and what
          happens once a trial or a plan runs out, show up in one place:{' '}
          <Link to="/docs/interface/settings">Settings → Licensing</Link>.
        </p>
        <DocShot
          slug="licensing"
          shot="license-states"
          alt="Settings → License showing active, trial-meter and used-up states"
          caption="The same Licensing card, in its three everyday states."
          callouts={[
            { n: 1, label: 'Active — plan name, masked key, Deactivate' },
            { n: 2, label: 'Trial — a usage meter and Go unlimited' },
            { n: 3, label: 'Used up — an amber Limit reached banner with Upgrade and Deactivate' },
          ]}
        />
      </DocSection>

      <DocSection id="activating" title="Activating a key">
        <p>
          Paste your license key into the field under Settings → Licensing and click{' '}
          <span style={kbd}>Apply</span>. ClipRadar registers this machine on your license and the
          card switches to its active state: plan name, a masked copy of your key, and a{' '}
          <strong>Deactivate</strong> button. You can activate a paid key without deactivating a
          trial first — the paid key simply takes over.
        </p>
      </DocSection>

      <DocSection id="one-machine" title="How many machines you can use">
        <p>
          A <strong>free trial</strong> activates on <strong>one machine</strong>. A{' '}
          <strong>paid license</strong> activates on <strong>two machines</strong> at once — so you
          can run ClipRadar on, say, your desktop and your laptop from the same key.
        </p>
        <p>
          To move your license to a machine beyond those seats, click{' '}
          <span style={kbd}>Deactivate</span> under Settings → Licensing on a machine you're no longer
          using to free a seat, then activate the same key on the new one. Trying to activate past
          your machine limit returns "This license is already activated on another device," not a
          generic error.
        </p>
      </DocSection>

      <DocSection id="offline" title="Works offline">
        <p>
          Activation needs internet once. After that, ClipRadar re-validates your license
          periodically in the background, and if it can't reach the license server — no connection,
          or the server itself is down — a recently-validated license keeps working for{' '}
          <strong>3 days</strong> past its last successful check before search is blocked. A bad
          key, an expired plan, or a limit you've actually hit is reported immediately rather than
          waiting out that grace period.
        </p>
      </DocSection>

      <DocSection id="blocked-search" title="When search is blocked">
        <p>
          A blocked search isn't a bug — it's ClipRadar telling you plainly that you've used up
          your trial, your plan lapsed, or you're not licensed yet. Under the hood it's an HTTP 402
          response, not an error, and the app shows a proper license-required dialog with a way
          forward instead of a red banner. Trial-exhausted, expired, suspended, offline-past-grace,
          and unlicensed all surface as distinct, named states, so you always know exactly which one
          you're in.
        </p>
        <DocShot
          slug="licensing"
          shot="license-required-dialog"
          alt="The license-required dialog shown when a search is blocked"
          caption="Search blocked, not broken — the dialog says why and what to do next."
        />
        <p>
          If a search seems to be failing for a different reason entirely, see{' '}
          <Link to="/docs/troubleshooting/cant-search">I can't search</Link>.
        </p>
      </DocSection>

      <DocSection id="upgrade" title="Trial → paid upgrade">
        <p>
          While you're on the trial, the Licensing card carries a{' '}
          <span style={kbd}>Go unlimited</span> button; once the 40 searches run out, the same spot
          becomes <span style={kbd}>Upgrade</span>. Both take you to ClipRadar's pricing — paste the
          key you get back into the same field described above and you're active immediately, no
          reinstall needed.
        </p>
      </DocSection>
    </>
  );
}
